// T-249: selezione delle allerte sonda *realmente attive* per dashboard/badge.
//
// Un `probe_alert` è un evento append-only emesso dal cron `probe-rules` ogni
// ~15 min FINCHÉ la condizione (sonda silente, fuori range, ecc.) persiste.
// Gli eventi sono immutabili: non esiste un evento "risolta". Di conseguenza
// contare gli eventi grezzi (`alerts.length`) tiene il badge "allerta attiva"
// acceso per sempre, anche per allerte di giorni prima o per frigoriferi nel
// frattempo archiviati.
//
// Un'allerta è "attiva" SOLO se è davvero in corso. La deriviamo con tre regole:
//   1. l'unità di conservazione esiste ed è NON archiviata (archiviare un frigo
//      deve spegnerne le allerte);
//   2. è recente: l'ultimo `probe_alert` per quell'unità cade entro una finestra
//      di freschezza (il cron ne emette uno ogni ~15 min mentre il problema
//      dura, quindi l'assenza di repliche recenti = problema risolto/stantio);
//   3. una sola allerta per unità (la più recente) → il conteggio rappresenta i
//      frigoriferi in allarme, non il numero di righe-evento.

export interface ProbeAlertLike {
  id: string;
  client_ts?: string | null;
  payload: {
    storage_unit_id: string;
    severity?: string;
    message?: string;
    window_end?: string | null;
  };
}

export interface StorageUnitRef {
  id: string;
  archived_at?: string | null;
}

// 2h: largamente sopra la cadenza del cron (15 min) per tollerare device
// offline / sync ritardata, ma abbastanza stretto da spegnere i fantasmi.
export const ACTIVE_PROBE_ALERT_WINDOW_MS = 2 * 60 * 60 * 1000;

function alertTimeMs(a: ProbeAlertLike): number {
  // window_end è il riferimento canonico della valutazione (UTC); client_ts è
  // il fallback se il payload è di una versione precedente senza window_end.
  return Date.parse(a.payload?.window_end ?? a.client_ts ?? '');
}

/**
 * Ritorna le allerte sonda attive: al più una per unità (la più recente),
 * escludendo unità archiviate/inesistenti e allerte più vecchie di `windowMs`.
 * Funzione pura: nessuna dipendenza da clock/IO (il "now" è iniettato).
 */
export function selectActiveProbeAlerts<A extends ProbeAlertLike>(
  alerts: readonly A[],
  units: readonly StorageUnitRef[],
  nowMs: number,
  windowMs: number = ACTIVE_PROBE_ALERT_WINDOW_MS,
): A[] {
  const liveUnitIds = new Set(units.filter((u) => !u.archived_at).map((u) => u.id));

  const latestByUnit = new Map<string, A>();
  for (const a of alerts) {
    const uid = a.payload?.storage_unit_id;
    if (!uid || !liveUnitIds.has(uid)) continue; // archiviata o inesistente
    const ts = alertTimeMs(a);
    if (!Number.isFinite(ts)) continue; // timestamp illeggibile
    if (nowMs - ts > windowMs) continue; // stantia: nessuna replica recente
    const prev = latestByUnit.get(uid);
    if (!prev || ts > alertTimeMs(prev)) latestByUnit.set(uid, a);
  }
  return [...latestByUnit.values()];
}
